import { PeriodPlan } from '@/optimizer/periodPlan.type';
import {
    extractGroupedCoefficientData,
    normalizePeriodDate,
    toFiniteNumber,
} from '@/optimizer/helpers/periodPlanUtils';
import { OptimizedPlans } from '@analytical-alley/opt-wasm/optimizer/optimizer.interface';
import { getColumnValues, getColumnNames } from '@analytical-alley/opt-wasm';

export const modifyMonthlyPlan = (plan: OptimizedPlans) => {
    const columnNames = getColumnNames(plan.monthly);
    const numRows = plan.monthly.numRows;
    const rows: any[] = [];
    
    const columnData: Record<string, any[]> = {};
    columnNames.forEach(colName => {
        try {
            columnData[colName] = getColumnValues(plan.monthly, colName);
        } catch {
            columnData[colName] = new Array(numRows).fill(undefined);
        }
    });
    
    for (let i = 0; i < numRows; i++) {
        const row: any = {};
        columnNames.forEach(colName => {
            row[colName] = columnData[colName]?.[i];
        });
        rows.push(row);
    }

    const monthData = extractGroupedCoefficientData({
        table: plan.optimizedPlan,
        periodColumnNames: ['Month', 'ISO Month'],
    });

    const sortedMonthEntries = Object.entries(monthData)
        .map(([key, value]) => ({ key: Number(key), ...value }))
        .sort((a, b) => {
            if (!a.minDate && !b.minDate) return a.key - b.key;
            if (!a.minDate) return 1;
            if (!b.minDate) return -1;
            return a.minDate.localeCompare(b.minDate);
        });

    const sequentialMonthData: Record<number, (typeof monthData)[number]> = {};
    sortedMonthEntries.forEach((entry, idx) => {
        sequentialMonthData[idx + 1] = entry;
    });

    return rows.map((row, index) => {
        const transformed: any = { ...row };
        
        let monthNum =
            toFiniteNumber(transformed['Month']) ??
            toFiniteNumber(transformed['ISO Month']);
        
        if (monthNum === undefined || monthNum === null) {
            monthNum = index + 1;
        }
        
        transformed.periodIndex = monthNum;

        const monthEntry = sequentialMonthData[monthNum];
        if (monthEntry) {
            transformed.coefficient = monthEntry.coefficient;
            transformed.periodDate = monthEntry.minDate ??
                normalizePeriodDate(
                    transformed['Month starting'] ??
                        transformed['Date'] ??
                        transformed['Period name'],
                ) ?? transformed['Month starting'] ?? transformed['Date'];
        } else {
            transformed.coefficient =
                toFiniteNumber(transformed['Coefficient']) ?? 0;
            transformed.periodDate =
                normalizePeriodDate(
                    transformed['Month starting'] ??
                        transformed['Date'] ??
                        transformed['Period name'],
                ) ?? transformed['Month starting'] ?? transformed['Date'];
        }
        
        delete transformed['Month'];
        delete transformed['ISO Month'];
        delete transformed['Month starting'];
        delete transformed['Date'];
        delete transformed['Period name'];
        delete transformed['Coefficient'];
        return transformed as PeriodPlan;
    });
};
